'use client';

import { useMyLocation } from '@/hooks/location';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { BasicForm } from './BasicForm';
import Spinner from '@/components/utils/Spinner';
import BopisStorePicker from '@/components/Location/BopisStorePicker';
import { LocationSelectors } from '@/components/Location/LocationSelectors';
import ExpressLocationMap from '@/components/Location/ExpressLocationMap';
import { resolveThanaById } from '@/helpers/address-helper';
import useHeaderStore from '@/store/header';
import { IconCross } from '@/icons/IconCross';
import { IArea, IStoreLists } from '@/interfaces/store';
import { ILocation } from '@/interfaces/location';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';
import { useLocationIds } from '@/hooks/useLocationIds';
import { useLocationModalState } from '@/hooks/useLocationModalState';
import { useLocationModalQueries } from '@/hooks/useLocationModalQueries';
import { openNotification } from '@/helpers/mantine-notification-helper';

interface LocationModalProps {
  showLocationModal: boolean;
  setShowLocationModal: (show: boolean) => void;
  isMyLocation?: boolean;
  isBopis?: boolean;
  /** Auth token — when set AND modal is BOPIS, queries the authenticated
   *  /api/store/list/store-changed endpoint instead of the public list.
   *  Used by Checkout only; Header/PreHeaderStoreSelector/FilterPanel omit it. */
  accessToken?: string;
}

function LocationModal({
  showLocationModal,
  setShowLocationModal,
  isMyLocation = false,
  isBopis = false,
  accessToken,
}: Readonly<LocationModalProps>) {
  const { selectedStore, myLocation } = useDeliveryOption();
  const { divisionId, setDivisionId, districtId, setDistrictId, thanaId, setThanaId } = useLocationIds();

  const setSelectedStore = useHeaderStore((state) => state.setSelectedStore);
  const setMyLocation = useHeaderStore((state) => state.setMyLocation);
  const setIsStoreSelected = useHeaderStore((state) => state.setIsStoreSelected);
  const setIsExpressEnabled = useHeaderStore((state) => state.setIsExpressEnabled);
  const setSelectedStoreCodes = useHeaderStore((state) => state.setSelectedStoreCodes);
  const setSelectedStoreIds = useHeaderStore((state) => state.setSelectedStoreIds);

  // UX-GATE: deferred writes — clicking a store/thana inside the modal only
  // updates LOCAL state. The global Zustand store (selectedStore, myLocation,
  // isExpressEnabled, etc.) is only mutated on Confirm.
  const [draftSelectedStore, setDraftSelectedStore] = useState<IStoreLists | null>(null);

  const { data: userCurrentLatLong } = useMyLocation();

  const {
    districts,
    thanas,
    allLocationList,
    thanaData,
    storeList,
    isStoreListLoading,
    isStoreListFetching,
  } = useLocationModalQueries({
    showLocationModal,
    isBopis,
    divisionId,
    districtId,
    thanaId,
    myLocationValue: myLocation.value,
    userCurrentLatLong,
    accessToken,
  });

  const {
    storeSearchValue,
    setStoreSearchValue,
    findStore,
    pickedLocation,
    setPickedFromMap,
    bopisPickedLocation,
    setBopisPickedLocation,
    invalidMapPick,
    setInvalidMapPick,
    reset,
  } = useLocationModalState({
    isMyLocation,
    myLocation,
    showLocationModal,
    allLocationList,
    thanaData,
    setDivisionId,
    setDistrictId,
    setThanaId,
    setSelectedStore,
  });

  // Anchor the map pin to a thana's lat/lng. Two coordinate shapes:
  //   - Express: canonical {latitude, longitude} (pickedLocation)
  //   - Bopis:   Map-shape {longitude, latitude} (bopisPickedLocation)
  function anchorToFirstThana(thana: ILocation) {
    const lat = parseFloat(thana.Lat);
    const lng = parseFloat(thana.Long);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    if (isMyLocation) {
      setPickedFromMap({ latitude: lat, longitude: lng });
    } else {
      setBopisPickedLocation({ longitude: lng, latitude: lat });
    }
  }

  // BOPIS: after a district/thana filter lands, center the map on storeList[0];
  // fall back to the first dropdown thana if the list is empty.
  // prevSelectedStoreIdRef distinguishes picker hydration (skip) from a user
  // pick (allow centering on dropdown change).
  const lastCenteredFilterRef = useRef<string>('');
  const prevSelectedStoreIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isMyLocation) {
      return;
    }

    // Don't snap away from a selected store on the first render after
    // BopisStorePicker hydration.
    const isHydrationLike =
      !!draftSelectedStore?.Id &&
      prevSelectedStoreIdRef.current === undefined;
    prevSelectedStoreIdRef.current = draftSelectedStore?.Id;

    if (isHydrationLike) {
      return;
    }

    if (divisionId === '-1' && districtId === '-1') {
      lastCenteredFilterRef.current = '';
      return;
    }

    const filterKey = `${divisionId}|${districtId}`;

    if (lastCenteredFilterRef.current === filterKey) {
      return;
    }

    // Wait for the real refetch to settle; `placeholderData: previousData`
    // would otherwise let us lock on the previous filter's stores.
    if (isStoreListFetching) {
      return;
    }

    if (storeList && storeList.length > 0) {
      const first = storeList[0];
      const lat = Number(first?.Latitude);
      const lng = Number(first?.Longitude);

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        lastCenteredFilterRef.current = filterKey;
        setBopisPickedLocation({ longitude: lng, latitude: lat });
      }

      return;
    }

    const firstThanaOption = thanas?.find((t) => t?.value && t.value !== '-1');

    if (!firstThanaOption) {
      return;
    }

    const thanasList = allLocationList?.Thanas as ILocation[] | undefined;
    const matched = thanasList?.find((t) => t.Id === firstThanaOption.value);

    if (!matched) {
      return;
    }

    const lat = parseFloat(matched.Lat);
    const lng = parseFloat(matched.Long);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    lastCenteredFilterRef.current = filterKey;
    setBopisPickedLocation({ longitude: lng, latitude: lat });
  }, [isMyLocation, draftSelectedStore, storeList, isStoreListFetching, thanas, allLocationList, divisionId, districtId, setBopisPickedLocation]);

  // BOPIS: on modal re-open (closed → open), re-seed the map pin and
  // dropdowns from the persisted `selectedStore`. Express hydration lives in
  // useLocationModalState; BOPIS needs its own gate because the source of
  // truth is `selectedStore`, not myLocation.
  const lastSyncedStoreIdRef = useRef<string | undefined>(undefined);
  const wasBopisModalOpenRef = useRef(false);

  useEffect(() => {
    if (isMyLocation) {
      return;
    }

    if (showLocationModal && !wasBopisModalOpenRef.current) {
      // Closed → opened: reset the sync gate so the next render re-seeds.
      lastSyncedStoreIdRef.current = undefined;
    }
    wasBopisModalOpenRef.current = showLocationModal;

    if (!showLocationModal) {
      return;
    }

    const storeId = selectedStore?.Id;

    if (!storeId) {
      return;
    }

    if (lastSyncedStoreIdRef.current === storeId) {
      return;
    }

    const lat = Number(selectedStore?.Latitude);
    const lng = Number(selectedStore?.Longitude);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setBopisPickedLocation({ longitude: lng, latitude: lat });
    }

    lastSyncedStoreIdRef.current = storeId;
  }, [isMyLocation, showLocationModal, selectedStore, setBopisPickedLocation]);

  // UX-GATE: seed the BOPIS draft from the persisted `selectedStore` on modal
  // open so the picker shows the persisted selection as if the user re-clicked
  // it. Without this, a refresh + reopen would show an empty draft until the
  // user clicks again. Clear the draft on modal close.
  const wasDraftSeededRef = useRef(false);
  useEffect(() => {
    if (isMyLocation) {
      return;
    }

    if (!showLocationModal) {
      wasDraftSeededRef.current = false;
      setDraftSelectedStore(null);
      return;
    }

    if (wasDraftSeededRef.current) {
      return;
    }

    if (selectedStore?.Id) {
      setDraftSelectedStore(selectedStore);
      wasDraftSeededRef.current = true;
    }
  }, [isMyLocation, showLocationModal, selectedStore]);

  // Toast on invalid-pick (outside service area). Fires once per event —
  // `invalidMapPick` only flips true→false via `onDistrictChange`/`onThanaChange`.
  useEffect(() => {
    if (!invalidMapPick) {
      return;
    }

    openNotification(
      'danger',
      'Outside service area',
      'Please pick a location within the highlighted region.',
    );
  }, [invalidMapPick]);

  // Handlers
  function onDistrictChange(value: string) {
    setDistrictId(value);
    setThanaId('-1');
    setInvalidMapPick(false);

    if (value === '-1') {
      setBopisPickedLocation(null);

      if (isMyLocation) {
        setPickedFromMap(null);
      }

      return;
    }

    const districtsList = allLocationList?.Districts as unknown as Array<{ Id: string; ParentId: string }> | undefined;
    const divisionsList = allLocationList?.Divisions as unknown as Array<{ Id: string; ParentId: string }> | undefined;
    const district = districtsList?.find((d) => d.Id === value);
    const division = district ? divisionsList?.find((v) => v.Id === district.ParentId) : undefined;

    if (division) {
      setDivisionId(division.Id);
    }

    const thanasList = allLocationList?.Thanas as ILocation[] | undefined;
    const firstThana = (thanasList ?? []).find((t) => t?.ParentId === value);

    if (firstThana) {
      anchorToFirstThana(firstThana);
    }
  }

  function onThanaChange(value: string) {
    setInvalidMapPick(false);

    // UX-GATE: local-only writes — global `myLocation` / `isExpressEnabled`
    // are now committed on Confirm, not on thana pick.

    if (value === '-1') {
      setThanaId('-1');
      setBopisPickedLocation(null);

      if (isMyLocation) {
        setPickedFromMap(null);
      }

      return;
    }

    const districtsList = allLocationList?.Districts as unknown as Array<{ Id: string; ParentId: string }> | undefined;
    const divisionsList = allLocationList?.Divisions as unknown as Array<{ Id: string; ParentId: string }> | undefined;
    const thana = resolveThanaById(value, thanaData, allLocationList);

    if (!thana) {
      setThanaId(value);
      return;
    }

    const district = districtsList?.find((d) => d.Id === thana.ParentId);
    const division = district ? divisionsList?.find((v) => v.Id === district.ParentId) : undefined;

    setThanaId(thana.Id);

    if (district) {
      setDistrictId(district.Id);
    }

    if (division) {
      setDivisionId(division?.Id ?? '');
    }

    anchorToFirstThana(thana);
  }

  function onReset() {
    reset();
    // Clear `myLocation` first so the gated setters see the empty value,
    // then clear codes + ids in lockstep to keep both arrays consistent —
    // otherwise `selectedStoreIds` could stay stale while codes are empty.
    setMyLocation({} as IArea);
    setSelectedStoreCodes([]);
    setSelectedStoreIds([]);
    setIsExpressEnabled(false);
    setIsStoreSelected(false);
    setSelectedStore({} as IStoreLists);
  }

  // Mobile BOPIS auto-commit: the picker has no Confirm button on mobile, so
  // a store click must promote the local draft to the global Zustand store and
  // close the modal in one step. Wired into BopisStorePicker as
  // `onCommitAndClose`. Desktop still goes through the manual Confirm path.
  const handleCommitAndClose = useCallback(() => {
    if (draftSelectedStore?.Id) {
      setSelectedStore(draftSelectedStore);
      setIsExpressEnabled(false);
      setMyLocation({} as IArea);
      // Cascade no longer clears storeCodes — do it explicitly so BOPIS
      // commits don't leave stale Express codes behind.
      setSelectedStoreCodes([]);
      setSelectedStoreIds([]);
    }
    setShowLocationModal(false);
  }, [draftSelectedStore, setSelectedStore, setShowLocationModal, setIsExpressEnabled, setMyLocation, setSelectedStoreCodes, setSelectedStoreIds]);

  // BOPIS body stays mounted; the picker renders its own inline loader overlay
  // over the store-list panel so map, search, and dropdowns stay interactive
  // while the list refetches.
  let contentToRender;

  if (isMyLocation) {
    contentToRender = null;
  } else {
    contentToRender = (
      <BopisStorePicker
        districts={districts}
        thanas={thanas}
        districtId={districtId}
        thanaId={thanaId}
        onDistrictChange={onDistrictChange}
        onThanaChange={onThanaChange}
        selectedStore={draftSelectedStore ?? ({} as IStoreLists)}
        userCurrentLatLong={userCurrentLatLong}
        setSelectedStore={setDraftSelectedStore}
        setMyLocation={setMyLocation}
        setShowLocationModal={setShowLocationModal}
        showLocationModal={showLocationModal}
        isBopis={isBopis}
        storeList={storeList}
        isStoreListLoading={isStoreListLoading}
        findStore={findStore}
        storeSearchValue={storeSearchValue}
        onStoreSearchValueChange={setStoreSearchValue}
        Loader={LocationModal.Loader}
        bopisPickedLocation={bopisPickedLocation}
        setBopisPickedLocation={setBopisPickedLocation}
        onCommitAndClose={handleCommitAndClose}
      />
    );
  }

  return (
    <Modal
      open={showLocationModal}
      closeModal={() => {
        setStoreSearchValue('');
        setDivisionId('-1');
        setDistrictId('-1');
        setThanaId('-1');
        setBopisPickedLocation(null);
        setPickedFromMap(null);
        setInvalidMapPick(false);
        lastCenteredFilterRef.current = '';
        prevSelectedStoreIdRef.current = undefined;
        setDraftSelectedStore(null);
        setShowLocationModal(false);
      }}
      closeButton={false}
      className="w-full !p-1 lg:w-[1000px]"
      dialogClasses={'!pb-0'}
    >
      <div>
        <BasicForm onSubmit={findStore} className="mb-5 w-full">
          <div className="font-semibold">
            <div className="flex items-start justify-between">
              <div>
                {!isMyLocation && (
                  <>
                    <p className="whitespace-nowrap text-2xl font-bold text-black">Find a Apex Store</p>
                    <p className="text-sm font-normal text-gray-500">Discover nearby apex store</p>
                  </>
                )}
                {isMyLocation && (
                  <>
                    <p className="whitespace-nowrap text-2xl text-black">Search Location</p>
                    <p className="text-[1rem] font-normal text-gray-500">Select the location manually or directly from the map</p>
                  </>
                )}
              </div>
              <button
                className="cursor-pointer"
                style={{ paddingRight: 'unset' }}
                onClick={() => {
                  setStoreSearchValue('');
                  setDivisionId('-1');
                  setDistrictId('-1');
                  setThanaId('-1');
                  setBopisPickedLocation(null);
                  setPickedFromMap(null);
                  setInvalidMapPick(false);
                  // Reset BOPIS re-centering refs so re-opening the modal
                  // treats the next selectedStore as a fresh hydration.
                  lastCenteredFilterRef.current = '';
                  prevSelectedStoreIdRef.current = undefined;
                  setDraftSelectedStore(null);
                  setShowLocationModal(false);
                }}
              >
                <IconCross className="w-8 p-[5px]" />
              </button>
            </div>
            {isMyLocation && (
              <div className="max-full">
                <LocationSelectors
                  districts={districts}
                  thanas={thanas}
                  districtId={districtId}
                  thanaId={thanaId}
                  onDistrictChange={onDistrictChange}
                  onThanaChange={onThanaChange}
                />
              </div>
            )}
          </div>

          {isMyLocation && (
            <div>
              <ExpressLocationMap
                show={showLocationModal}
                pickedLocation={pickedLocation}
                userCurrentLatLong={userCurrentLatLong}
                thanas={(allLocationList?.Thanas as ILocation[]) ?? []}
                districts={
                  (allLocationList?.Districts as unknown as Array<{ Id: string; ParentId: string }> | null) ?? []
                }
                divisions={
                  (allLocationList?.Divisions as unknown as Array<{ Id: string; ParentId: string }> | null) ?? []
                }
                onPickFromMap={({ thanaId: tId, districtId: dId, divisionId: vId, lat, lng, invalidPick }) => {
                  const isInvalid = !!invalidPick;
                  setInvalidMapPick(isInvalid);

                  if (!isInvalid) {
                    if (tId !== '-1' && tId) {
                      setThanaId(tId);
                      // UX-GATE: map pick is local-only — global `myLocation`
                      // and `isExpressEnabled` are committed on Confirm.
                    }

                    if (dId) {
                      setDistrictId(dId);
                    }

                    if (vId) {
                      setDivisionId(vId);
                    }
                  }
                  // Invalid pick — leave dropdowns at last valid value.
                  // The setInvalidMapPick(true) above keeps Confirm disabled
                  // (see disabled gate on the Confirm button).

                  if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    setPickedFromMap({ latitude: lat, longitude: lng });
                  }
                }}
              />
            </div>
          )}
        </BasicForm>
        <div className={isBopis ? 'mt-2' : 'mt-2 max-h-[40vh] overflow-y-auto'}>
          {contentToRender}
        </div>
        <div className="flex w-full flex-col items-stretch gap-2 md:w-auto md:flex-row md:items-center md:justify-end">
          {isMyLocation && invalidMapPick && (
            <p
              data-testid="invalid-pick-warning"
              className="order-3 w-full text-sm text-red-500 md:order-1"
            >
              Selected location is outside our service area. Please pick a
              location within the highlighted region.
            </p>
          )}
          <button
            type="button"
            disabled={
              isMyLocation
                ? invalidMapPick || thanaId === '-1' || !thanaId
                : !draftSelectedStore?.Id
            }
            aria-label={
              isMyLocation && invalidMapPick
                ? 'Outside service area. Please pick a location within the highlighted region.'
                : isMyLocation && (thanaId === '-1' || !thanaId)
                ? 'Please select a thana before confirming.'
                : undefined
            }
            title={
              isMyLocation && invalidMapPick
                ? 'Selected location is outside service area'
                : isMyLocation && (thanaId === '-1' || !thanaId)
                ? 'Please select a thana before confirming'
                : undefined
            }
            onClick={() => {
              if (isMyLocation) {
                // Guard: don't write `myLocation = {value: '-1', label: 'All Thanas'}`
                // when the user hasn't picked a real thana. Production's
                // `onThanaOptionClick` bailed on the `-1'` sentinel — we mirror
                // that here so the Express confirm path can never corrupt the
                // global store.
                if (thanaId === '-1' || !thanaId) {
                  return;
                }

                // Fall back to resolveThanaById when the dropdown's `thanas`
                // is stale relative to `thanaId` (e.g. `thanaData` loading).
                const selectedThana = thanas.find((t) => t.value === thanaId);
                const resolvedThana =
                  selectedThana?.label ??
                  resolveThanaById(thanaId, thanaData, allLocationList)?.Name;

                if (!resolvedThana) {
                  return;
                }

                const thanaName = resolvedThana;
                setMyLocation({ value: thanaId, label: thanaName } as IArea);
                setIsExpressEnabled(true);
                setSelectedStore({} as IStoreLists);
                setIsStoreSelected(false);
                reset();
              } else {
                // UX-GATE: BOPIS commit-from-draft. The picker writes the
                // draft on row click; here we promote the draft to the global
                // store on Confirm. Guard: only commit when the draft has a
                // real store id (the row click sets it).
                if (draftSelectedStore?.Id) {
                  setSelectedStore(draftSelectedStore);
                  setIsExpressEnabled(false);
                  setMyLocation({} as IArea);
                }
                // Do NOT call reset() — it would clear `selectedStore`.
              }
              setShowLocationModal(false);
            }}
            className="order-1 w-full bg-red-500 px-6 py-2 font-semibold text-white disabled:opacity-50 md:order-3 md:w-auto"
          >
            Confirm Location
            {isMyLocation && invalidMapPick && (
              <span className="sr-only">
                {' '}- Outside service area. Please pick a location within the highlighted region.
              </span>
            )}
            {isMyLocation && !invalidMapPick && (thanaId === '-1' || !thanaId) && (
              <span className="sr-only">
                {' '}- Please select a thana before confirming.
              </span>
            )}
          </button>
          <button
            type="button"
            aria-label="Reset location and store selection"
            title="Reset"
            onClick={onReset}
            className="order-2 w-full border border-red-500 bg-white px-6 py-2 font-semibold text-red-500 md:w-auto md:order-2"
          >
            Reset
          </button>
        </div>
      </div>
    </Modal>
  );
}

LocationModal.Loader = () => {
  return (
    <div className="flex h-full w-full items-center justify-center pb-16">
      <Spinner size={40} />
    </div>
  );
};


export default LocationModal;
